import { Link } from '@tanstack/react-router';
import { CircleArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { useGetWaitListStatus } from '~entities/studios/api/get-wait-list-status';
import { useUpdateWaitListStatus } from '~entities/studios/api/update-wait-list-status';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Label } from '~shared/shadcn/ui/label';
import { Switch } from '~shared/shadcn/ui/switch';

export function CrmEmailSyncSettings() {
  const { data: waitListStatus, isLoading: isLoadingStudio } =
    useGetWaitListStatus();
  const updateWaitListStatus = useUpdateWaitListStatus();

  const syncEnabled = waitListStatus?.ghlEmailSyncEnabled !== false;

  const handleUpdateGhlEmailSync = async (status: boolean) => {
    try {
      await updateWaitListStatus.mutateAsync({
        ghlEmailSyncEnabled: status,
      });
      toast.success(
        `CRM email sync ${status ? 'enabled' : 'disabled'} successfully`,
      );
    } catch {
      toast.error(`Error ${status ? 'enabling' : 'disabling'} CRM email sync`);
    }
  };

  return (
    <div className='p-4'>
      <Link to='/dashboard' className='mb-8 flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <span className='text-lg font-semibold'>Back to Dashboard</span>
      </Link>

      <Card className='mx-auto max-w-4xl'>
        <CardHeader>
          <CardTitle>CRM Email Sync</CardTitle>
          <CardDescription>
            Copy emails sent from the portal into the matching contact&apos;s
            CRM conversation in GoHighLevel.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='rounded-lg border bg-card p-5'>
            <div className='flex items-start justify-between gap-4'>
              <div className='space-y-1'>
                <Label htmlFor='studio-ghl-email-sync' className='text-sm'>
                  Sync portal emails to CRM conversations
                </Label>
                <p className='text-xs text-muted-foreground'>
                  When off, emails sent to parents are still delivered but no
                  longer appear in the contact&apos;s GHL conversation thread.
                </p>
              </div>
              <Switch
                id='studio-ghl-email-sync'
                checked={syncEnabled}
                disabled={isLoadingStudio || updateWaitListStatus.isPending}
                onCheckedChange={(checked) => {
                  void handleUpdateGhlEmailSync(checked);
                }}
              />
            </div>
          </div>
          <p className='text-xs text-muted-foreground'>
            Emails that previously failed to sync are listed under{' '}
            <Link
              to='/payment/portal-crm-email-sync-issue'
              className='text-blue-600 underline-offset-2 hover:underline'
            >
              Portal to CRM Email Sync Issue
            </Link>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
